import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import {
  getLMSCatalogs,
  getLMSCourses,
  catalogSelect,
  courseSelect,
  sessionSelect,
  eventSelect,
} from "../../Redux/Actions/coursesAction";
import CourseSessionEventModal from "./courseSessionEventModal";
import ScheduledTrainingConfirmModal from "./ScheduledTrainingConfirmModal";
import DropDown from "../../components/DropDown";
import Loader from "./../../components/Loader";
import "./../../config";

const PLACEHOLDER = "Dummy";

function DodeboScheduledTraining() {
  const [isLoading, setIsLoading] = useState(true);

  const [catalogs, setCatalogs] = useState([]);
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [events, setEvents] = useState([]);

  const [catalog, setCatalog] = useState(null);
  const [course, setCourse] = useState(null);
  const [session, setSession] = useState(null);
  const [event, setEvent] = useState(null);

  const navigate = useNavigate();
  const baseApiUrl = global.config.baseApiUrl;

  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const {
    catalog: currentCatalog,
    course: currentCourse,
    session: currentSession,
  } = useSelector((state) => state.courses);
  const { user: currentUser } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  // Initial catalog list
  useEffect(() => {
    async function getDoceboCatalogs() {
      const failed = false;
      const url = `${baseApiUrl}/courses?name=catalog`;
      const reqBody = { userId: currentUser.id, accessToken: currentUser.accessToken };

      const data = await axios.post(url, reqBody);

      dispatch(getLMSCatalogs(data, failed));

      const catalogsInitial = [
        { label: "Select a Course Catalog", value: PLACEHOLDER, selectedCatalog: {} },
      ];
      const mapped =
        data.data.catalogs?.map(({ catalog_id, catalog_name, catalog_code }) => ({
          label: `${catalog_id} - ${catalog_code} - ${catalog_name}`,
          value: catalog_id,
          selectedCatalog: {
            catalog_id: JSON.stringify(catalog_id),
            catalog_name,
          },
        })) ?? [];

      setCatalogs([...catalogsInitial, ...mapped]);
      setIsLoading(false);

      localStorage.removeItem("currentCatalog");
      localStorage.removeItem("currentCourse");
      localStorage.removeItem("currentSession");
      localStorage.removeItem("currentEvent");
    }

    getDoceboCatalogs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- load catalogs once on mount (same as original)

  // Courses when catalog changes
  useEffect(() => {
    if (!catalog || catalog === PLACEHOLDER || !currentCatalog?.catalog_id) {
      setCourses([{ label: "Select a ILT Course", value: PLACEHOLDER, selectedCourse: {} }]);
      setSessions([]);
      setEvents([]);
      return;
    }

    async function getDoceboCourses() {
      const failed = false;
      const url = `${baseApiUrl}/courses?name=catalog_course`;
      const catalogId = currentCatalog.catalog_id;
      const reqBody = {
        catalogId,
        instructorId: currentUser.id,
        accessToken: currentUser.accessToken,
      };

      const data = await axios.post(url, reqBody);

      dispatch(getLMSCourses(data, failed));

      const coursesInitial = [
        { label: "Select a ILT Course", value: PLACEHOLDER, selectedCourse: {} },
      ];
      let mapped = [];
      if (data.data.courses != null && data.data.courses.length > 0) {
        mapped = data.data.courses.map(({ course_id, course_name, course_type }) => ({
          label: `${course_id} - ${course_name} - ${course_type}`,
          value: course_id,
          selectedCourse: {
            course_id,
            course_name,
            course_type,
          },
        }));
      }

      setCourses([...coursesInitial, ...mapped]);
      setSessions([]);
      setEvents([]);
    }

    getDoceboCourses();
  }, [catalog, currentCatalog, baseApiUrl, currentUser.id, currentUser.accessToken, dispatch]);

  // Sessions when course changes
  useEffect(() => {
    if (!course || course === PLACEHOLDER || !currentCourse?.course_id) {
      setSessions([
        { label: "Select a Classroom Session", value: PLACEHOLDER, selectedSession: {} },
      ]);
      setEvents([]);
      return;
    }

    async function getDoceboSessions() {
      const url = `${baseApiUrl}/sessions`;
      const courseId = currentCourse.course_id;
      const reqBody = {
        courseId,
        instructorId: currentUser.id,
        accessToken: currentUser.accessToken,
      };

      const data = await axios.post(url, reqBody);

      const sessionsInitial = [
        { label: "Select a Classroom Session", value: PLACEHOLDER, selectedSession: {} },
      ];
      const sessionList = data.data.sessions ?? [];
      const mapped = sessionList.map(
        ({ session_id, session_name, course_id: cid, session_date_begin, session_date_end }) => ({
          label: `(${cid}) ${session_id} - ${session_name} - ${session_date_begin} - ${session_date_end}`,
          value: session_id,
          selectedSession: {
            session_id,
            session_name,
            session_date_begin,
            session_date_end,
          },
        }),
      );

      setSessions([...sessionsInitial, ...mapped]);
      setEvents([]);
    }

    getDoceboSessions();
  }, [course, currentCourse, baseApiUrl, currentUser.id, currentUser.accessToken]);

  // Events when session changes
  useEffect(() => {
    if (!session || session === PLACEHOLDER || !currentSession?.session_id) {
      setEvents([{ label: "Select a Classroom Event", value: PLACEHOLDER, selectedEvent: {} }]);
      return;
    }

    async function getDoceboEvents() {
      const url = `${baseApiUrl}/events`;
      const sessionId = JSON.stringify(currentSession.session_id);
      const reqBody = { sessionId, accessToken: currentUser.accessToken };

      const data = await axios.post(url, reqBody);

      const eventsInitial = [
        { label: "Select a Classroom Event", value: PLACEHOLDER, selectedEvent: {} },
      ];
      const eventList = data.data.events ?? [];
      const mapped = eventList.map(
        ({
          event_id,
          event_name,
          session_id: sid,
          event_date,
          event_time,
          event_venue,
          session_score_base,
          session_evaluation_type,
          session_min_attended_events_for_completion,
        }) => ({
          label: `(${sid}) ${event_id} - ${event_name} - ${event_date} - ${event_time} - ${event_venue}`,
          value: event_id,
          selectedEvent: {
            event_id,
            event_name,
            event_date,
            event_time,
            event_venue,
            session_score_base,
            session_evaluation_type,
            session_min_attended_events_for_completion,
          },
        }),
      );

      setEvents([...eventsInitial, ...mapped]);
    }

    getDoceboEvents();
  }, [session, currentSession, baseApiUrl, currentUser.accessToken]);

  const handleCatalogChange = (e) => {
    const selected = catalogs.find((c) => c.value == e.currentTarget.value);
    dispatch(catalogSelect(selected.selectedCatalog));
    setCatalog(e.currentTarget.value);
    setCourse(null);
    setSession(null);
    setEvent(null);
  };

  const handleCourseChange = (e) => {
    const selected = courses.find((c) => c.value == e.currentTarget.value);
    dispatch(courseSelect(selected.selectedCourse));
    setCourse(e.currentTarget.value);
    setSession(null);
    setEvent(null);
  };

  const handleSessionChange = (e) => {
    const selected = sessions.find((s) => s.value == e.currentTarget.value);
    dispatch(sessionSelect(selected.selectedSession));
    setSession(e.currentTarget.value);
    setEvent(null);
  };

  const handleEventChange = (e) => {
    const selected = events.find((ev) => ev.value == e.currentTarget.value);
    dispatch(eventSelect(selected.selectedEvent));
    setEvent(e.currentTarget.value);
  };

  const onCourseSessionEventFormSubmit = (e) => {
    e.preventDefault();
    handleClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    navigate("/manageRoster", { replace: true });
  };

  const confirmDisabled = !catalog || !course || !session || !event;

  return (
    <>
      {isLoading ? (
        <div style={{ width: "80px", margin: "auto" }}>
          <Loader />
        </div>
      ) : (
        <div className="col-md-12">
          <DropDown
            label="Select Course Catalog:"
            value={catalog}
            options={catalogs}
            onChange={handleCatalogChange}
          />

          <DropDown
            label="Select Course:"
            disabled={!catalog || !(courses.length > 1)}
            value={course}
            options={courses}
            onChange={handleCourseChange}
          />

          <DropDown
            label="Select Session:"
            disabled={!course || !(sessions.length > 1)}
            value={session}
            options={sessions}
            onChange={handleSessionChange}
          />

          <DropDown
            label="Select Event:"
            disabled={!session || !(events.length > 1)}
            value={event}
            options={events}
            onChange={handleEventChange}
          />
        </div>
      )}

      <br />

      <div className="d-flex align-items-center justify-content-center">
        <Button variant="primary" onClick={handleShow} disabled={confirmDisabled}>
          Confirm
        </Button>
      </div>

      <ScheduledTrainingConfirmModal show={show} onHide={handleClose} onContinue={handleSubmit}>
        <CourseSessionEventModal onSubmit={onCourseSessionEventFormSubmit} />
      </ScheduledTrainingConfirmModal>
    </>
  );
}

export default DodeboScheduledTraining;
